import { DirectUploadProvider } from 'react-activestorage-provider'
import MediaPreview from 'Shared/components/MediaPreview'
import './FileUploader.scoped.scss'

function isTypeAllowed(fileType, allowedTypes) {
  return allowedTypes.some(allowed =>
    allowed.endsWith('/*')
      ? fileType.startsWith(allowed.slice(0, -1))
      : fileType === allowed
  )
}

function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`
  return `${bytes}B`
}

export default function FileUploader({
  onUploaded,
  existingFilename,
  existingUrl,
  enablePreview = false,
  multiple = false,
  maxSize = null,
  inline = false,
  onError = (_msg) => {},
  allowedTypes = null,
  fileTypeError = null,
  sizeError = null,
  isVolatile = false,
  validateAsync = null,
}) {
  const [filenames, setFilenames] = useState([])
  const fileInputRef = useRef(null)
  const [draggingOverTarget, setDraggingOverTarget] = useState(false)

  const handleClick = () => {
    fileInputRef.current.click()
  }

  const uploadedFilename = existingFilename || (existingUrl && existingUrl.split('/').pop())

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOverTarget(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOverTarget(false)
  }

  const validate = (files) => {
    if(maxSize) {
      for(const file of files) {
        if(file.size > maxSize) {
          onError(sizeError || `File size too large (max ${formatFileSize(maxSize)})`)
          return false;
        }
      }
    }

    if(allowedTypes) {
      for(const file of files) {
        if(!isTypeAllowed(file.type, allowedTypes)) {
          onError(fileTypeError || 'File type not allowed')
          return false;
        }
      }
    }

    return true
  }

  const acceptAttribute = allowedTypes ? allowedTypes.join(',') : undefined

  return <DirectUploadProvider
    directUploadsPath={ `/rails/active_storage/direct_uploads/${isVolatile ? 'amazon_volatile' : 'amazon'}` }
    onSuccess={(ids) => onUploaded(ids, multiple ? filenames : filenames[0])}
    render={({ handleUpload, uploads, ready }) => {
      const handleDrop = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDraggingOverTarget(false)
        const files = e.dataTransfer.files
        if(!validate(files)) return
        if(validateAsync) {
          const error = await validateAsync(Array.from(files))
          if(error) { onError(error); return; }
        }
        setFilenames(Array.from(files).map((f) => f.name))
        handleUpload(files)
      }

      return (
        <div className="image-uploader">
          <div className={`drag-target${draggingOverTarget ? ' active' : ''}${inline ? ' inline' : ''}`}
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragEnter}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}>
            <SvgIconsUpload />
            <span>Drag and drop or click to upload</span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            multiple={multiple}
            disabled={!ready}
            accept={acceptAttribute}
            onChange={async e => {
              const files = e.currentTarget.files
              if(!validate(files)) return
              if(validateAsync) {
                const error = await validateAsync(Array.from(files))
                if(error) { onError(error); return; }
              }
              setFilenames(Array.from(files).map((f) => f.name))
              handleUpload(files)
            }}
          />

          {uploadedFilename &&
            <p className="no-margin-top">Existing file: {uploadedFilename}</p>
          }

          {enablePreview && existingUrl &&
            <MediaPreview url={existingUrl} />
          }

          {uploads.map(upload => {
            switch (upload.state) {
              case 'waiting':
                return <p key={upload.id}>Waiting to upload {upload.file.name}</p>
              case 'uploading':
                return (
                  <p key={upload.id}>
                    Uploading {upload.file.name}: {upload.progress}%
                  </p>
                )
              case 'error':
                return (
                  <p key={upload.id}>
                    Error uploading {upload.file.name}: {upload.error}
                  </p>
                )
              case 'finished':
                return (
                  <p key={upload.id}>Finished uploading {upload.file.name}</p>
                )
            }
          })}
        </div>
      )
    }}
  />
}

